import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { ArrowRight, Check } from "lucide-react";
import { Reveal } from "@/components/site/Reveal";
import { SPORTS } from "@/lib/data";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const initial = { name: "", email: "", sport: "", message: "", department: "representation" };

const DEPARTMENTS = [
  {
    id: "representation",
    label: "Athlete Representation & General",
    email: "info@dvgsports.net",
    blurb: "Athlete, family or general inquiry — tell us your goals. Every relationship starts with a conversation.",
  },
  {
    id: "marketing",
    label: "Brand Partnerships & Marketing",
    email: "dajamaxey@dvgsports.net",
    blurb: "Brands and partners — let's build NIL and marketing collaborations with our athletes.",
  },
];

export const ContactForm = () => {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const dept = DEPARTMENTS.find((d) => d.id === form.department) || DEPARTMENTS[0];

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      setForm(initial);
      toast.success("Message sent. We'll be in touch shortly.");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative py-28 lg:py-40 bg-[#050505] border-t border-white/10">
      <div className="max-w-[1500px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-14">
        <div className="lg:col-span-5">
          <Reveal>
            <span className="text-[12px] uppercase tracking-[0.32em] text-[#cbd1d8]">Get In Touch</span>
            <h2 className="font-display text-6xl lg:text-8xl leading-[0.9] mt-5 text-white">
              Let's build<br />your <span className="text-stroke-volt">legacy.</span>
            </h2>
            <p className="text-white/55 text-lg mt-8 max-w-md leading-relaxed">{dept.blurb}</p>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          {done ? (
            <motion.div
              data-testid="contact-success"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="border border-[#cbd1d8]/40 bg-[#cbd1d8]/5 p-12 flex flex-col items-start gap-5"
            >
              <div className="w-14 h-14 bg-[#cbd1d8] text-black flex items-center justify-center"><Check size={26} /></div>
              <h3 className="font-display text-4xl text-white">Message received.</h3>
              <p className="text-white/60">Thank you for reaching out to DVG Sports. A member of our team will be in touch shortly.</p>
              <button data-testid="contact-reset" onClick={() => setDone(false)} className="text-[#cbd1d8] uppercase tracking-[0.14em] text-sm hover:underline">Send another</button>
            </motion.div>
          ) : (
            <Reveal>
              <form data-testid="contact-form" onSubmit={submit} className="space-y-8">
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-white/45 mb-3">I'm reaching out about</label>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {DEPARTMENTS.map((d) => (
                      <button
                        key={d.id}
                        type="button"
                        data-testid={`contact-dept-${d.id}`}
                        onClick={() => setForm((f) => ({ ...f, department: d.id }))}
                        className={`text-left px-5 py-4 border transition-colors duration-300 ${form.department === d.id ? "bg-[#cbd1d8] text-black border-[#cbd1d8]" : "border-white/20 text-white/70 hover:border-white/50"}`}
                      >
                        <span className="block text-sm font-bold uppercase tracking-[0.1em] leading-tight">{d.label}</span>
                      </button>
                    ))}
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <Field label="Full Name *" testid="contact-name" value={form.name} onChange={set("name")} />
                  <Field label="Email *" type="email" testid="contact-email" value={form.email} onChange={set("email")} />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-white/45 mb-3">Sport</label>
                  <select
                    data-testid="contact-sport"
                    value={form.sport}
                    onChange={set("sport")}
                    className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#cbd1d8] outline-none transition-colors [&>option]:bg-[#111] [&>option]:text-white"
                  >
                    <option value="">Select</option>
                    {SPORTS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-[0.2em] text-white/45 mb-3">Message *</label>
                  <textarea
                    data-testid="contact-message"
                    rows={4}
                    value={form.message}
                    onChange={set("message")}
                    className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#cbd1d8] outline-none transition-colors resize-none"
                  />
                </div>
                <button
                  data-testid="contact-submit"
                  type="submit"
                  disabled={loading}
                  className="group inline-flex items-center gap-3 bg-[#cbd1d8] text-black font-bold uppercase tracking-[0.14em] text-sm px-10 py-4 hover:bg-white transition-colors duration-300 disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Send Message"}
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            </Reveal>
          )}
        </div>
      </div>
    </section>
  );
};

const Field = ({ label, type = "text", testid, value, onChange }) => (
  <div>
    <label className="block text-[11px] uppercase tracking-[0.2em] text-white/45 mb-3">{label}</label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={onChange}
      className="w-full bg-transparent border-b border-white/20 py-3 text-white focus:border-[#cbd1d8] outline-none transition-colors"
    />
  </div>
);
